import { type AuthTypeS } from '@/features/Auth/outbound/share';
import {
  CertificationApiS,
  CertificationConstantS,
  CertificationSchemaS,
  CertificationValidationS,
} from '@/features/Certification/outbound/share';
import { SnsApiS } from '@/features/Sns/outbound/share';
import type { ErrorJson } from '@/libs/errors';
import { isFeatureError } from '@/libs/errors/FeatureError';
import { AT, T } from '@/libs/translation';
import { isValidEmail } from '@/libs/validation/email';
import { Button, Input, InputChangeEvent, InputTimerEndEvent, Label, Typo } from '@/ui/component/atom';
import { Header, Layout, Modal, Toast } from '@/ui/component/cell';
import { EmailDuplicatedView, LogoView, makeErrorPopup, makePopup } from '@/ui/component/view';
import { debounce, getSharedData, type SharedData } from '@/ui/util';
import { fromEntries, pipe } from '@fxts/core';
import { isAxiosError } from 'axios';
import { $, type Html, html, Page, View } from 'rune-ts';
import auth_klass from '../Common/Auth.module.scss';
import klass from './SnsVerifyEmailPage.module.scss';

export interface SnsVerifyEmailPageData {
  url: string;
  sns_request_referer?: string;
}

export class SnsVerifyEmailPageBodyView extends View<SnsVerifyEmailPageData> {
  requested_code = false;
  component: {
    email: Input;
    code: Input;
    request_code: Button;
    submit: Button;
  };

  constructor(data: SnsVerifyEmailPageData) {
    super(data);

    this.component = {
      email: new Input(
        {},
        {
          attr: {
            name: 'email',
            type: 'email',
            inputmode: 'email',
            placeholder: T('auth.login.email_placeholder'),
            required: true,
          },
          helper: {
            validators: [
              {
                message: T('auth.signup.email_invalid_guide'),
                validator: (email) => isValidEmail(email),
              },
            ],
          },
        },
      ),
      code: new Input(
        {},
        {
          attr: {
            name: 'code',
            type: 'text',
            placeholder: T('auth.signup.code_placeholder'),
            maxlength: 6,
            required: true,
            inputmode: 'numeric',
            autocomplete: 'off',
          },
          helper: {
            validators: [
              {
                message: T('auth.signup.code_time_end'),
              },
            ],
          },
          controller: {
            type: 'timer',
            timer: {
              minutes: 8,
              autoplay: false,
            },
          },
          klass: `${klass.code} ${klass.disabled}`,
          delete_pattern: /\D/g,
        },
      ),
      request_code: new Button(
        {
          label: new Typo({ text: T('auth.signup.request_email_verify_code') }, { typo: 'p2', weight: 'bold' }),
        },
        {
          size: 'small',
          type: 'button',
          klass: `${klass.button} request_code`,
          disabled: true,
        },
      ),
      submit: new Button(
        {
          label: new Typo({ text: T('common.ok') }, { weight: 'bold' }),
        },
        {
          disabled: true,
          size: 'large',
        },
      ),
    };
  }

  override template() {
    const { theme, device } = getSharedData(this);

    return html` <div class="${auth_klass.page}">
      <div class="${auth_klass.container}">
        ${new LogoView({ theme })}
        <div class="${auth_klass.section}">
          <form class="${klass.verify} verify">
            <div class="${klass.container}">
              <div class="${klass.guide_container}">
                ${new Typo(
                  { text: html.preventEscape(T('auth.verify_email.sns_guide', { context: device })) },
                  { weight: 'bold' },
                )}
                ${new Typo({
                  text: html.preventEscape(T('auth.verify_email.sns_guide_description', { context: device })),
                })}
              </div>
              <div class="${klass.input_container}">
                ${new Label({
                  input: this.component.email,
                  button: this.component.request_code,
                })}
                ${this.component.code}
              </div>
              <div class="${klass.button_container}">
                ${new Button(
                  {
                    label: new Typo({ text: T('common.back') }, { weight: 'bold' }),
                  },
                  {
                    color: 'line',
                    size: 'large',
                    klass: `${klass.button} back`,
                    type: 'button',
                  },
                )}
                ${this.component.submit}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>`;
  }

  override onRender() {
    this.component.email.focus();
    const changeHandler = debounce(this.formChangeHandler.bind(this), 100);
    this.delegate(InputTimerEndEvent, Input, this.timerEndHandler);
    this.delegate(InputChangeEvent, Input, changeHandler);
    this.delegate('input', '.verify', changeHandler);
    this.delegate('click', '.request_code', this.requestCodeHandler);
    this.delegate('submit', '.verify', this.formSubmitHandler);
    this.delegate('click', '.back', () => {
      if (this.data.sns_request_referer) {
        location.replace(this.data.sns_request_referer);
      } else {
        history.back();
      }
    });
  }

  private getForm() {
    return $(this.element()).find('.verify')?.element() as HTMLFormElement;
  }

  private async timerEndHandler() {
    const res = await makePopup({
      title: T('auth.signup.code_timer_done'),
      message: T('auth.signup.code_timer_done_description'),
      ok: T('auth.signup.code_retry'),
      cancel: T('common.cancel'),
    });

    this.component.submit.option.disabled = true;
    this.component.submit.redraw();

    if (res === 'confirm') {
      return this.requestCodeHandler();
    }
  }

  private async requestCodeHandler() {
    try {
      await pipe(
        {
          email: this.component.email.value,
          certification_type: CertificationConstantS.CERTIFICATION_TYPE.EMAIL,
        },
        CertificationValidationS.validateCreateEmailCertificationCheckEmailReqBody,
        CertificationApiS.postEmailCertificationCheckEmail,
      );

      if (!this.requested_code) {
        this.requested_code = true;

        this.component.request_code.data.label = new Typo(
          { text: T('auth.signup.request_email_verify_code_retry') },
          { typo: 'p2', weight: 'bold' },
        );
        this.component.request_code.option.color = 'secondary';
        this.component.request_code.redraw();

        this.component.code.option.klass = `${klass.code} ${klass.fade_in}`;
        this.component.code.redraw();
      }

      this.component.code.inputControllerView.restart();

      Toast.open({ label: T('common.send_email_with_certification_code'), close_label: T('common.close') }, {});
    } catch (e) {
      if (isFeatureError(e)) {
        return makeErrorPopup(e, e.json);
      }

      if (isAxiosError<ErrorJson>(e)) {
        if (e.response?.data.code === 'SIGNUP_002' && isAxiosError<AuthTypeS.SignupErrorType>(e)) {
          const res = await Modal.open(
            { view: new EmailDuplicatedView({ email: this.component.email.value, error: e.response.data }) },
            {
              header: { title: T('auth.signup.email_duplicated') },
              footer: {
                cancel: T('common.cancel'),
                confirm: T('auth.signup.go_login'),
              },
              backdrop: true,
            },
          );

          if (res === 'confirm') {
            const { client_id } = getSharedData(this);
            location.replace(`/${AT.language}/login?email=${this.component.email.value}&client_id=${client_id}`);
          }

          return;
        } else {
          return makeErrorPopup(e, e.response?.data);
        }
      }

      return makeErrorPopup(e);
    }
  }
  private formChangeHandler(_e: Event) {
    const form = this.getForm();
    const form_data = fromEntries(new FormData(form).entries());

    const code_request_res = pipe(
      { ...form_data, certification_type: CertificationConstantS.CERTIFICATION_TYPE.EMAIL },
      CertificationSchemaS.CreateEmailCertificationCheckEmailReqBody.safeParse,
    );

    if (code_request_res.success && this.component.request_code.option.disabled) {
      this.component.request_code.enabled();
    }
    if (!code_request_res.success && !this.component.request_code.option.disabled) {
      this.component.request_code.disabled();
    }

    if (this.requested_code) {
      const res = pipe(
        { ...form_data, certification_type: CertificationConstantS.CERTIFICATION_TYPE.EMAIL },
        CertificationSchemaS.VerifyEmailCertificationReqBody.safeParse,
      );

      if (this.component.submit.option.disabled && res.success) {
        this.component.submit.enabled();
      }

      if (!this.component.submit.option.disabled && !res.success) {
        this.component.submit.disabled();
      }
    }
  }
  private async formSubmitHandler(e: Event & { originalEvent: Event }) {
    e.originalEvent.preventDefault();

    try {
      const form = this.getForm();
      const form_data = fromEntries(new FormData(form).entries());

      await pipe(
        { ...form_data, certification_type: CertificationConstantS.CERTIFICATION_TYPE.EMAIL },
        CertificationValidationS.validateVerifyEmailCertificationReqBody,
        SnsApiS.postVerifyEmail,
      );

      location.replace(this.data.url);
    } catch (e) {
      if (isFeatureError(e)) {
        return makeErrorPopup(e, e.json);
      }

      if (isAxiosError(e)) {
        return makeErrorPopup(e, e.response?.data);
      }

      return makeErrorPopup(e);
    }
  }
}

export class SnsVerifyEmailPage extends Page<SnsVerifyEmailPageData> {
  layout: Layout;

  constructor(data: SnsVerifyEmailPageData, sharedData: SharedData) {
    super(data, sharedData);

    this.layout = new Layout(
      { view: new SnsVerifyEmailPageBodyView(this.data) },
      {
        fullwidth: true,
        header: sharedData.device === 'mo' ? new Header({}) : undefined,
      },
    );
  }

  protected override template(): Html {
    return html` <div>${this.layout}</div> `;
  }

  override onRender() {
    this.delegate(Header.HeaderBackEvent, Header, () => {
      if (this.data.sns_request_referer) {
        location.replace(this.data.sns_request_referer);
      } else {
        history.back();
      }
    });
  }
}
